import { ReactNode, useMemo, useState } from "react";
import { RichContent } from "./utils";
import { debounce, isEqual } from "lodash";
import SavingIndicator from "./SavingIndicator";

export enum AutoSaveStatus {
  IDLE = "IDLE",
  SAVING = "SAVING",
  JUST_SAVED = "JUST_SAVED",
  // ERROR = "ERROR",
}

type Output = {
  savingInProgress: boolean;
  editorContent: RichContent | null;
  setEditorContent: (c: RichContent | null) => void;
  lastEditSec: number | null;
  savingIndicator: ReactNode;
  forceSave: () => Promise<void>;
  isDirty: boolean;
  autoSaveStatus: AutoSaveStatus;
};

export default (
  initialContent: string | null,
  initialLastEditSec: number | null,
  doSave: (c: RichContent) => Promise<void>,
  hasError: boolean = false,
  updateBufferMs: number = 2500,
  contentType: string | null = "json",
  justSavedTimeoutMs: number = 2500,
): Output => {
  const [saveState, setSaveState] = useState<{
    status: AutoSaveStatus;
    isDirty: boolean;
  }>({
    status: AutoSaveStatus.IDLE,
    isDirty: false,
  });
  const [content, setContent] = useState<RichContent | null>(null);
  const [lastEditSec, setLastEditSec] = useState<number | null>(
    initialLastEditSec,
  );

  const update = async (c: RichContent | null) => {
    setSaveState({ status: AutoSaveStatus.SAVING, isDirty: false }); // reset the dirty state BEFORE saving
    if (c) {
      await doSave(c);
    }
    setLastEditSec(Math.floor(Date.now() / 1000));
    setSaveState({ status: AutoSaveStatus.JUST_SAVED, isDirty: false });

    // Reset to IDLE after justSavedTimeoutMs
    setTimeout(() => {
      setSaveState((s) =>
        s.status === AutoSaveStatus.JUST_SAVED
          ? { ...s, status: AutoSaveStatus.IDLE }
          : s,
      );
    }, justSavedTimeoutMs);
  };

  // Use the debounce function to save data with a delay
  const debouncedUpdate = useMemo(
    () => debounce(update, updateBufferMs, { trailing: true }), // Adjust the delay as needed (e.g., 1000ms = 1 second)
    [],
  );

  const setEditorContent = (c: RichContent | null) => {
    const _contentType =
      contentType === "json" || contentType === null ? "json" : "html";
    let _initialContent =
      initialContent && _contentType === "json"
        ? JSON.parse(initialContent)
        : contentType === "html" && initialContent
          ? initialContent
          : null;

    let _newContent =
      _contentType === "json" && c?.json
        ? JSON.parse(c.json)
        : c?.html
          ? c?.html
          : null;
    if (isEqual(_initialContent, _newContent)) {
      return;
    }
    setSaveState({ status: AutoSaveStatus.SAVING, isDirty: true });
    setContent(c);
    debouncedUpdate(c);
  };

  const forceSave = async () => {
    debouncedUpdate.cancel();
    await update(content);
  };

  const isSaving = saveState.status === AutoSaveStatus.SAVING;

  return {
    savingInProgress: isSaving,
    editorContent: content,
    setEditorContent,
    lastEditSec,
    savingIndicator: (
      <SavingIndicator
        saving={isSaving}
        lastUpdatedSec={lastEditSec}
        hasError={hasError}
      />
    ),
    forceSave,
    isDirty: saveState.isDirty,
    autoSaveStatus: saveState.status,
  };
};
